import {ArrowUpRightIcon, CommentIcon, RocketIcon, XIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Dialog, Heading, IconButton, Label} from '@primer/react'
import {useRef} from 'react'

import {MergeBoxPanelContent} from './MergeBoxPanelContent'
import type {Channels} from '../hooks/use-mergeability-live-updates'
import {isFeatureEnabled} from '@github-ui/feature-flags'

interface MergeabilitySidesheetProps {
  mergeabilitySidesheetIsOpen: boolean
  toggleMergeabilitySidesheet: (isOpen: boolean) => void
  mergeStatusButtonRef: React.RefObject<HTMLButtonElement | null>
  viewerLogin: string
  helpUrl: string
  channels: Channels
}

/**
 * Renders the merge box in a sidesheet that is triggered from the header
 */
export function MergeabilitySidesheet({
  mergeabilitySidesheetIsOpen,
  toggleMergeabilitySidesheet,
  mergeStatusButtonRef,
  viewerLogin,
  helpUrl,
  channels,
}: MergeabilitySidesheetProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  const exitOverlay = () => toggleMergeabilitySidesheet?.(false)
  if (!mergeabilitySidesheetIsOpen) {
    return null
  }

  return (
    <Dialog
      initialFocusRef={closeButtonRef}
      position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
      returnFocusRef={mergeStatusButtonRef}
      onClose={exitOverlay}
      title="Merge status"
      renderHeader={() => (
        <Dialog.Header>
          <div className="d-flex flex-justify-between flex-items-center mx-2">
            <Heading as="h3" className="f5 text-bold">
              Merge status
            </Heading>
            <div className="d-flex flex-items-center gap-1">
              <MergeabilitySidesheetFeedback />
              <IconButton
                ref={closeButtonRef}
                aria-label="Close merge status"
                icon={XIcon}
                variant="invisible"
                onClick={exitOverlay}
              />
            </div>
          </div>
        </Dialog.Header>
      )}
    >
      <Dialog.Body className="p-0">
        <MergeBoxPanelContent helpUrl={helpUrl} viewerLogin={viewerLogin} channels={channels} />
      </Dialog.Body>
    </Dialog>
  )
}

function MergeabilitySidesheetFeedback() {
  const feedbackEnabled = isFeatureEnabled('merge_status_header_feedback')

  if (!feedbackEnabled) {
    return null
  }

  return (
    <ActionMenu>
      <ActionMenu.Button variant="invisible" size="small">
        <Label variant="success">Preview</Label>
      </ActionMenu.Button>
      <ActionMenu.Overlay>
        <ActionList>
          <ActionList.LinkItem
            href="https://gh.io/merge-status-header-changelog"
            target="_blank"
            rel="noopener noreferrer"
          >
            <ActionList.LeadingVisual>
              <RocketIcon />
            </ActionList.LeadingVisual>
            What&apos;s new
            <ActionList.TrailingVisual>
              <ArrowUpRightIcon />
            </ActionList.TrailingVisual>
          </ActionList.LinkItem>
          <ActionList.LinkItem
            href="https://gh.io/merge-status-header-feedback"
            target="_blank"
            rel="noopener noreferrer"
          >
            <ActionList.LeadingVisual>
              <CommentIcon />
            </ActionList.LeadingVisual>
            Give feedback
            <ActionList.TrailingVisual>
              <ArrowUpRightIcon />
            </ActionList.TrailingVisual>
          </ActionList.LinkItem>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
